import { Box, IconButton } from "@mui/material";
import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ReactComponent as ChevronLeftIcon } from "../../assets/chevron-left-double.svg";
import { NAVBAR_HEIGHT, NavDesktop } from "../Navbar/NavDesktop";
import { NavMobile } from "../Navbar/NavMobile";
import { Sidebar, DRAWER_WIDTH, DRAWER_WIDTH_CLOSED } from "../Navbar/Sidebar";
import useScreenSize from "../../hooks/useScreenSize";
import { PageHeader } from "../PageHeader/PageHeader";
import { Breadcrumbs } from "../Breadcrumbs/Breadcrumbs";
import { usePageMetadataContext } from "../../context/PageMetadataContext";
import { PageUid, useCustomisation } from "../../context/CustomisationContext";
import { resolvePageStyle } from "../../utils/pageStyleResolver";
import { AssignmentLayoutContext } from "../../context/AssignmentLayoutContext";
import { LayoutControlContext } from "../../context/LayoutControlContext";
import { usePageCustomisation } from "../../hooks/usePageCustomisation";
import { hasSomeCustomisation } from "../../utils/helpers";
import { GlobalLoader } from "../GlobalLoader/GlobalLoader";
import { gray } from "../../theme/themePrimitives";
import { PackageType } from "../../services/api/types/assignments/assignment";

export type MessageType = "none" | "success" | "error";

const ASSIGNMENT_SIDEBAR_WIDTH_CLOSED = 56;

interface PageLayoutProps {
  children: React.ReactNode;
  initialSidebarOpen?: boolean;
  showMobileNav?: boolean;
  mobileNavTitle?: string;
  contentSx?: React.CSSProperties | any;
  isErrorComponent?: boolean;
  sidebarProps?: {
    showMessage?: MessageType;
    setShowMessage?: React.Dispatch<React.SetStateAction<MessageType>>;
    assignmentDataFilled?: boolean;
    setOpenMessageModal?: React.Dispatch<React.SetStateAction<boolean>>;
    setNavigateTo?: React.Dispatch<React.SetStateAction<string>>;
  };
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  initialSidebarOpen = false,
  showMobileNav,
  mobileNavTitle,
  contentSx,
  sidebarProps,
  isErrorComponent = false,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
  const {
    breadcrumbs: pageBreadcrumbs,
    pageMetadata,
    pageStyle,
  } = usePageMetadataContext();
  usePageCustomisation("GPC");
  const { isMobile, isTablet } = useScreenSize();
  const isMobileView = showMobileNav ?? (isMobile || isTablet);
  const location = useLocation();
  const { customisations, getCustomisationValue } = useCustomisation();
  const isGpcLoaded = hasSomeCustomisation(
    customisations.customisations,
    "GPC",
  );

  const [contextDisableNavigation, setContextDisableNavigation] =
    useState(false);

  const navigationDisabled = isErrorComponent || contextDisableNavigation;

  const isRunReportRoute = [
    "/reports/user-report",
    "/reports/user-activity-report",
    "/reports/assignment-activity-report",
    "/reports/assesment-report",
  ].some((route) => location.pathname.includes(route));

  const isAssignmentRoute = location.pathname.startsWith("/assignment");
  const isProfileOrCertificates =
    location.pathname.startsWith("/profile") ||
    location.pathname.startsWith("/certificates");

  // Assignment desktop: right sidebar (course content) aligns with the main
  // content's top edge; state for context
  const [assignmentSidebarContent, setAssignmentSidebarContent] =
    useState<React.ReactNode>(null);
  const [assignmentSidebarOpen, setAssignmentSidebarOpen] = useState(true);
  const [assignmentPackageType, setAssignmentPackageType] = useState<
    PackageType | undefined
  >(undefined);
  // PDF and Video assignments share the same rounded-card sidebar styling
  // (vs. SCORM's flush/no-padding styling) — add new types here as needed.
  const isCardStyled =
    assignmentPackageType === PackageType.PdfUpload ||
    assignmentPackageType === PackageType.VideoUpload;
  useEffect(() => {
    if (!isAssignmentRoute) {
      setAssignmentSidebarContent(null);
      setAssignmentSidebarOpen(true);
      setAssignmentPackageType(undefined);
    }
  }, [isAssignmentRoute]);

  const useAssignmentGridLayout =
    isAssignmentRoute && !isMobileView && !isTablet;

  const contentMarginLeft = isMobileView
    ? 0
    : sidebarOpen
      ? DRAWER_WIDTH
      : DRAWER_WIDTH_CLOSED;

  // Check if we're on the home route
  const isHomeRoute = location.pathname.startsWith("/home");
  const isProfileRoute = location.pathname.startsWith("/profile");
  const isCertificatesRoute = location.pathname.startsWith("/certificates");
  // Check if we're on an error route (to disable page-specific background)
  const isErrorRoute = location.pathname.startsWith("/error");

  const designPageUid: PageUid | null = isCertificatesRoute
    ? "CERTIFICATES"
    : isProfileRoute
      ? "PROFILE"
      : null;

  const resolvedTitleColor = designPageUid
    ? resolvePageStyle(designPageUid, getCustomisationValue).titleColor
    : pageStyle?.titleColor;

  const isPageDesignReady = designPageUid
    ? hasSomeCustomisation(customisations.customisations, designPageUid)
    : true;

  const pageBackgroundSettings = useMemo(() => {
    if (!isHomeRoute && !isProfileRoute && !isCertificatesRoute) return null;

    const {
      HOME: { backgroundSettings: homeBg },
      PROFILE: { backgroundSettings: profileBg },
      CERTIFICATES: { backgroundSettings: certBg },
      GPC: { backgroundSettings: gpcBg },
    } = customisations.customisations;

    // Pick which page settings to use based on route
    const pageBg = isHomeRoute
      ? homeBg
      : isProfileRoute
        ? profileBg
        : isCertificatesRoute
          ? certBg
          : null;

    if (!pageBg && !gpcBg) return null;

    // Read the correct keys explicitly based on route
    const backgroundColor = isHomeRoute
      ? (pageBg?.homePrimaryBgColor ?? gpcBg?.gpcPrimaryBgColor)
      : isProfileRoute
        ? (pageBg?.profilePrimaryBgColor ?? gpcBg?.gpcPrimaryBgColor)
        : isCertificatesRoute
          ? (pageBg?.certificatesPrimaryBgColor ?? gpcBg?.gpcPrimaryBgColor)
          : gpcBg?.gpcPrimaryBgColor;

    const backgroundImageToggle = isHomeRoute
      ? pageBg?.homeBackgroundImageToggle === "true"
      : isProfileRoute
        ? pageBg?.profileBackgroundImageToggle === "true"
        : isCertificatesRoute
          ? pageBg?.certificatesBackgroundImageToggle === "true"
          : false;

    const backgroundImage = isHomeRoute
      ? pageBg?.homeBackgroundImage
      : isProfileRoute
        ? pageBg?.profileBackgroundImage
        : isCertificatesRoute
          ? pageBg?.certificatesBackgroundImage
          : undefined;

    const shouldUseBackgroundImage =
      backgroundImageToggle && !!backgroundImage?.trim();

    const backgroundImageTransparency = isHomeRoute
      ? (pageBg?.homeBackgroundImageTransparency ??
        gpcBg?.gpcBackgroundImageTransparency)
      : isProfileRoute
        ? (pageBg?.profileBackgroundImageTransparency ??
          gpcBg?.gpcBackgroundImageTransparency)
        : isCertificatesRoute
          ? (pageBg?.certificatesBackgroundImageTransparency ??
            gpcBg?.gpcBackgroundImageTransparency)
          : gpcBg?.gpcBackgroundImageTransparency;

    const transparencyPercentage = backgroundImageTransparency
      ? Number(backgroundImageTransparency) / 100
      : 0;

    return {
      backgroundColor,
      backgroundImage: shouldUseBackgroundImage ? `/${backgroundImage}` : null,
      transparencyPercentage,
      shouldUseBackgroundImage,
    };
  }, [
    isHomeRoute,
    isProfileRoute,
    isCertificatesRoute,
    customisations.customisations,
  ]);

  const mainBackgroundColor = isErrorComponent
    ? "#F3F4F6"
    : isRunReportRoute
      ? "#2B2B2B"
      : pageBackgroundSettings?.shouldUseBackgroundImage
        ? "transparent"
        : pageBackgroundSettings?.backgroundColor || gray[100];

  if (!isGpcLoaded) return <GlobalLoader />;

  return (
    <LayoutControlContext.Provider
      value={{
        isErrorComponent: navigationDisabled,
        setIsErrorComponent: setContextDisableNavigation,
      }}
    >
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // backgroundColor: gray[100],
          position: "relative",
        }}
      >
        {pageBackgroundSettings?.shouldUseBackgroundImage && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              marginLeft: `${contentMarginLeft}px`,
              marginTop: isMobileView ? 0 : NAVBAR_HEIGHT,
              padding: useAssignmentGridLayout
                ? "0 0 0 32px"
                : isAssignmentRoute
                  ? "12px 32px 32px 32px"
                  : "32px",
              backgroundImage: `url(${pageBackgroundSettings.backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: -1,
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: `rgba(0,0,0,${pageBackgroundSettings.transparencyPercentage})`,
              },
            }}
          />
        )}
        {isMobileView ? (
          <NavMobile
            title={
              (mobileNavTitle ?? pageMetadata?.title) ||
              (isHomeRoute ? "Home" : undefined)
            }
            setSidebarOpen={setSidebarOpen}
          />
        ) : (
          <NavDesktop
            sidebarOpen={sidebarOpen}
            isErrorComponent={navigationDisabled}
            pageBgColor={mainBackgroundColor}
          />
        )}
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          mobileView={isMobileView}
          isErrorComponent={navigationDisabled}
          {...sidebarProps}
        />
        <Box
          component="main"
          sx={{
            marginLeft: `${contentMarginLeft}px`,
            marginTop: isMobileView ? 0 : NAVBAR_HEIGHT,
            flex: 1,
            padding: useAssignmentGridLayout
              ? "0 0 0 32px"
              : isAssignmentRoute
                ? isMobileView
                  ? "0px"
                  : "12px 32px 32px 32px"
                : "32px",
            overflow: "auto",
            position: "relative",
            zIndex: 1,
            backgroundColor: mainBackgroundColor,
            transition: (theme) =>
              theme.transitions.create("margin-left", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            minHeight: `calc(100vh - ${NAVBAR_HEIGHT})`,
            ...contentSx,
          }}
        >
          {useAssignmentGridLayout ? (
            <AssignmentLayoutContext.Provider
              value={{
                sidebarContent: assignmentSidebarContent,
                setSidebarContent: setAssignmentSidebarContent,
                sidebarOpen: assignmentSidebarOpen,
                setSidebarOpen: setAssignmentSidebarOpen,
                packageType: assignmentPackageType,
                setPackageType: setAssignmentPackageType,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  pt: 4,
                  minHeight: `calc(100vh - ${NAVBAR_HEIGHT})`,
                  minWidth: 0,
                }}
              >
                {!isErrorRoute &&
                  pageBreadcrumbs &&
                  pageBreadcrumbs?.length > 0 && (
                    <Breadcrumbs
                      items={pageBreadcrumbs}
                      previewText={
                        !pageMetadata?.title
                          ? pageMetadata?.previewText
                          : undefined
                      }
                    />
                  )}
                {!isErrorRoute && pageMetadata?.title && isPageDesignReady && (
                  <PageHeader
                    title={pageMetadata.title}
                    titleColor={resolvedTitleColor}
                    previewText={pageMetadata.previewText}
                    titleBadge={pageMetadata.titleBadge}
                  />
                )}
                {/* Both columns are siblings below the breadcrumbs/header so
                    the sidebar (collapsed or expanded) starts at the same
                    vertical position as the main content. */}
                <Box sx={{ display: "flex", minWidth: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
                  <Box
                    sx={{
                      position: "relative",
                      flexShrink: 0,
                      minWidth: 0,
                      // Collapse to 0 while the package type is unknown so
                      // the main content column gets the full width instead
                      // of being squeezed to make room for a sidebar
                      width:
                        assignmentPackageType === undefined
                          ? 0
                          : assignmentSidebarOpen
                            ? "30%"
                            : `${ASSIGNMENT_SIDEBAR_WIDTH_CLOSED}px`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: assignmentSidebarOpen ? "stretch" : "center",
                      overflow: "hidden",
                      borderLeft: "1px solid #E5E7EB",
                      margin: isCardStyled ? "16px 0 0 0" : "0",
                      borderTop: "1px solid #E5E7EB",
                      borderRadius: isCardStyled ? "12px" : "0",
                      visibility:
                        assignmentPackageType === undefined
                          ? "hidden"
                          : "visible",
                      backgroundColor: "#fff",

                      transition: (theme) =>
                        theme.transitions.create("width", {
                          easing: theme.transitions.easing.sharp,
                          duration: assignmentSidebarOpen
                            ? theme.transitions.duration.enteringScreen
                            : theme.transitions.duration.leavingScreen,
                        }),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: assignmentSidebarOpen
                          ? "flex-end"
                          : "center",
                        width: "100%",
                        p: isCardStyled ? "14px" : "6px",
                        background: "#fff",
                      }}
                    >
                      <IconButton
                        aria-label={
                          assignmentSidebarOpen
                            ? "Collapse sidebar"
                            : "Expand sidebar"
                        }
                        onClick={() =>
                          setAssignmentSidebarOpen((prev) => !prev)
                        }
                        sx={{
                          flexShrink: 0,
                          color: gray[500],
                          border: "none",
                          "&:hover": {
                            color: gray[900],
                          },
                        }}
                      >
                        <ChevronLeftIcon
                          style={{
                            width: 20,
                            height: 20,
                            transform: assignmentSidebarOpen
                              ? "none"
                              : "scaleX(-1)",
                          }}
                        />
                      </IconButton>
                    </Box>
                    {assignmentSidebarOpen && (
                      <Box
                        sx={{
                          minWidth: 0,
                          overflow: "auto",
                          flex: 1,
                          borderTop: `1px solid ${gray[300]}`,
                        }}
                      >
                        {assignmentSidebarContent}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </AssignmentLayoutContext.Provider>
          ) : (
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                maxWidth: isProfileOrCertificates ? 1440 : undefined,
                margin: isProfileOrCertificates ? "0 auto" : undefined,
                color: isProfileOrCertificates
                  ? pageStyle?.textColor
                  : undefined,
              }}
            >
              {!isErrorRoute &&
                pageBreadcrumbs &&
                pageBreadcrumbs?.length > 0 &&
                !isAssignmentRoute && <Breadcrumbs items={pageBreadcrumbs} />}
              {!isErrorRoute &&
                pageMetadata?.title &&
                !isMobileView &&
                isPageDesignReady && (
                  <PageHeader
                    title={pageMetadata.title}
                    titleColor={resolvedTitleColor}
                    previewText={pageMetadata.previewText}
                    titleBadge={pageMetadata.titleBadge}
                  />
                )}
              {children}
            </Box>
          )}
        </Box>
      </Box>
    </LayoutControlContext.Provider>
  );
};
